const IMAGE_SELECTOR = '[data-js-native-lazy] img[loading="lazy"]';
const WRAPPER_SELECTOR = '[data-js-native-lazy]';
const REVEAL_SELECTOR = '[data-scroll-reveal]';
const REVEAL_COMPLETE_EVENT = 'hei:scrollRevealComplete';
const PENDING_CLASS = 'js-native-lazy--pending';
const LOADED_CLASS = 'js-native-lazy--loaded';
const ERROR_CLASS = 'js-native-lazy--error';
const MIN_PLACEHOLDER_DURATION = 100;

const findElements = (root, selector) => {
    const scope = root instanceof Element || root instanceof Document ? root : document;
    const elements = scope.matches?.(selector) ? [scope] : [];

    return elements.concat(Array.from(scope.querySelectorAll(selector)));
};

const getWrapper = (img) => img.closest(WRAPPER_SELECTOR);

const getPendingReveal = (img) => {
    if (
        !document.documentElement.classList.contains('scroll-reveal-enabled')
        || document.documentElement.classList.contains('scroll-reveal-fallback')
        || window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
        return null;
    }

    const reveal = getWrapper(img)?.closest(REVEAL_SELECTOR);

    if (!reveal || reveal.dataset.scrollRevealComplete === 'true') {
        return null;
    }

    if (reveal.dataset.scrollRevealInit !== 'true') {
        return null;
    }

    return reveal;
};

const markPending = (img, resetTimer = false) => {
    const wrapper = getWrapper(img);

    if (!wrapper) {
        return;
    }

    if (resetTimer || !img.dataset.jsNativeLazyPendingStarted) {
        img.dataset.jsNativeLazyPendingStarted = `${Date.now()}`;
    }

    wrapper.classList.add(PENDING_CLASS);
    wrapper.classList.remove(LOADED_CLASS, ERROR_CLASS);
};

const applyLoaded = (img) => {
    const wrapper = getWrapper(img);

    if (!wrapper) {
        return;
    }

    wrapper.classList.remove(PENDING_CLASS, ERROR_CLASS);
    wrapper.classList.add(LOADED_CLASS);
    img.classList.add('lazy-loaded');
};

const markLoaded = (img, skipMinimumDuration = false) => {
    if (getPendingReveal(img)) {
        img.dataset.jsNativeLazyReady = 'true';
        return;
    }

    if (skipMinimumDuration) {
        applyLoaded(img);
        return;
    }

    const started = Number.parseInt(img.dataset.jsNativeLazyPendingStarted ?? '0', 10);
    const elapsed = started ? Date.now() - started : MIN_PLACEHOLDER_DURATION;
    const remaining = Math.max(0, MIN_PLACEHOLDER_DURATION - elapsed);

    if (remaining === 0) {
        applyLoaded(img);
        return;
    }

    window.setTimeout(() => {
        applyLoaded(img);
    }, remaining);
};

const markError = (img) => {
    const wrapper = getWrapper(img);

    if (!wrapper) {
        return;
    }

    wrapper.classList.remove(PENDING_CLASS, LOADED_CLASS);
    wrapper.classList.add(ERROR_CLASS);
};

const lazy = {
    mutationObserver: null,
    revealCompletionObserver: false,

    init(root = document) {
        window.clearTimeout(window.__heiJsNativeLazyFallback);
        document.documentElement.classList.add('js-native-lazy-enabled');
        document.documentElement.classList.remove('js-native-lazy-fallback');

        this.refresh(root);
        this.observeDom();
        this.observeRevealCompletions();
    },

    refresh(root = document) {
        findElements(root, IMAGE_SELECTOR).forEach((img) => {
            this.prepareImage(img);
        });
    },

    releaseDeferredImages(root) {
        findElements(root, IMAGE_SELECTOR).forEach((img) => {
            if (img.dataset.jsNativeLazyReady !== 'true') {
                return;
            }

            img.dataset.jsNativeLazyReady = 'false';

            if (img.complete) {
                if (img.naturalWidth > 0) {
                    markLoaded(img, true);
                } else {
                    markError(img);
                }
            }
        });
    },

    prepareImage(img, forcePending = false) {
        if (!(img instanceof HTMLImageElement) || !img.matches(IMAGE_SELECTOR)) {
            return;
        }

        const wrapper = getWrapper(img);

        if (!wrapper) {
            return;
        }

        if (!forcePending && (wrapper.classList.contains(LOADED_CLASS) || wrapper.classList.contains(ERROR_CLASS))) {
            return;
        }

        markPending(img, forcePending);

        if (img.complete) {
            if (img.naturalWidth > 0) {
                markLoaded(img);
            } else {
                markError(img);
            }

            return;
        }

        if (img.dataset.jsNativeLazyObserved === 'true') {
            return;
        }

        img.addEventListener('load', () => markLoaded(img));
        img.addEventListener('error', () => markError(img));
        img.dataset.jsNativeLazyObserved = 'true';
    },

    observeDom() {
        if (this.mutationObserver || !document.body) {
            return;
        }

        this.mutationObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'childList') {
                    mutation.addedNodes.forEach((node) => {
                        if (!(node instanceof Element)) {
                            return;
                        }

                        if (node.matches(IMAGE_SELECTOR)) {
                            this.prepareImage(node);
                        }

                        findElements(node, IMAGE_SELECTOR).forEach((img) => {
                            this.prepareImage(img);
                        });
                    });
                }

                if (mutation.type === 'attributes' && mutation.target.matches(IMAGE_SELECTOR)) {
                    this.prepareImage(mutation.target, true);
                }
            });
        });

        this.mutationObserver.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['src', 'srcset'],
        });
    },

    observeRevealCompletions() {
        if (this.revealCompletionObserver) {
            return;
        }

        document.addEventListener(REVEAL_COMPLETE_EVENT, (event) => {
            this.releaseDeferredImages(event.target);
        });

        this.revealCompletionObserver = true;
    },
};

export default lazy;
